import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { 
  DollarSign, 
  Cpu, 
  CreditCard, 
  Zap, 
  BarChart3, 
  History, 
  CheckCircle2,
  AlertCircle,
  TrendingUp,
  ExternalLink,
  Info
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { ScrollArea } from '../ui/scroll-area';
import { Badge } from '../ui/badge';

const PRICING_TIERS = [
  {
    name: 'Starter',
    price: '$0',
    description: 'Perfect for small teams and prototyping',
    features: ['Up to 5 Users', 'Basic Analytics', 'Standard Support', '5GB Storage'],
    highlight: false
  },
  {
    name: 'Pro',
    price: '$49',
    description: 'For growing businesses and advanced needs',
    features: ['Unlimited Users', 'Advanced Analytics', 'Priority Support', '50GB Storage', 'Custom Branded UI'],
    highlight: true
  },
  {
    name: 'Enterprise',
    price: 'Custom',
    description: 'Strategic solutions for large organizations',
    features: ['SLA Guarantee', 'Dedicated Manager', 'Unlimited Storage', 'API Access', 'SSO & Advanced Security'],
    highlight: false
  }
];

export default function BillingPricingPanel() {
  const [apiCosts, setApiCosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalMonthlyCost, setTotalMonthlyCost] = useState(450.00); // Mocked but will add logic

  useEffect(() => {
    async function fetchBillingData() {
      setLoading(true);
      try {
        const { data: apiData } = await supabase
          .from('api_usage_logs')
          .select('*')
          .order('created_at', { ascending: false });

        if (apiData) {
          const formattedApiCosts = apiData.map(log => ({
            id: log.id,
            name: log.service_name,
            usage: log.usage_metric,
            cost: log.estimated_cost,
            date: new Date(log.created_at).toLocaleDateString()
          }));
          setApiCosts(formattedApiCosts);
          
          const total = apiData.reduce((acc, log) => acc + (log.estimated_cost || 0), 0);
          setTotalMonthlyCost(total);
        }
      } catch (err) {
        console.error('Error fetching billing data:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchBillingData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-zinc-50 dark:bg-zinc-950">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-xs font-medium text-zinc-500">Loading billing details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-zinc-50/50 dark:bg-zinc-950/50">
      {/* Header */}
      <div className="px-8 py-6 border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/50 backdrop-blur-md sticky top-0 z-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h2 className="text-2xl font-black text-zinc-900 dark:text-zinc-50 tracking-tight">Billing & Pricing</h2>
            <p className="text-xs text-zinc-500 mt-1 font-medium italic">Manage your subscription, API usage, and billing history</p>
          </div>
          <div className="flex items-center gap-3 bg-blue-50/50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-900/20 p-3 rounded-2xl px-5">
            <div className="p-2 rounded-xl bg-blue-500/10 text-blue-600 dark:text-blue-400">
              <TrendingUp className="w-4 h-4" />
            </div>
            <div>
              <p className="text-[10px] uppercase font-black tracking-widest text-zinc-400">Monthly Forecast</p>
              <p className="text-xl font-black text-zinc-900 dark:text-zinc-50">${totalMonthlyCost.toFixed(2)}</p>
            </div>
          </div>
        </div>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-8 space-y-10 max-w-6xl mx-auto">
          
          {/* Pricing Tiers */}
          <section>
            <div className="flex items-center gap-2 mb-6 ml-1">
              <Zap className="w-5 h-5 text-amber-500 fill-amber-500/20" />
              <h3 className="text-lg font-bold text-zinc-800 dark:text-zinc-100">Subscription Plans</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {PRICING_TIERS.map((tier) => (
                <div 
                  key={tier.name}
                  className={cn(
                    "relative p-8 rounded-3xl border transition-all duration-300 group",
                    tier.highlight 
                      ? "bg-white dark:bg-zinc-900 border-blue-500 dark:border-blue-500 shadow-xl shadow-blue-500/10 scale-[1.02]" 
                      : "bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 hover:border-zinc-300 dark:hover:border-zinc-700 shadow-sm"
                  )}
                >
                  {tier.highlight && (
                    <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-500 text-white font-black px-4 py-1 rounded-full text-[10px] uppercase tracking-tighter">
                      Most Popular
                    </Badge>
                  )}
                  <div className="mb-6">
                    <h4 className="text-sm font-black text-zinc-500 uppercase tracking-widest mb-1">{tier.name}</h4>
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-black text-zinc-900 dark:text-zinc-50 tracking-tighter">{tier.price}</span>
                      {tier.price !== 'Custom' && <span className="text-zinc-400 text-sm font-bold">/month</span>}
                    </div>
                    <p className="text-xs text-zinc-400 mt-2 leading-relaxed">{tier.description}</p>
                  </div>
                  <ul className="space-y-3 mb-8">
                    {tier.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-2.5 text-xs text-zinc-600 dark:text-zinc-300 font-medium">
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <button className={cn(
                    "w-full py-3 rounded-2xl text-[11px] font-black uppercase tracking-widest transition-all",
                    tier.highlight
                      ? "bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-600/20"
                      : "bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-700 dark:text-zinc-200"
                  )}>
                    {tier.name === 'Enterprise' ? 'Contact Sales' : 'Get Started'}
                  </button>
                </div>
              ))}
            </div>
          </section>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* API Breakdown */}
            <section className="space-y-4">
              <div className="flex items-center justify-between ml-1">
                <div className="flex items-center gap-2">
                  <Cpu className="w-5 h-5 text-violet-500 fill-violet-500/20" />
                  <h3 className="text-lg font-bold text-zinc-800 dark:text-zinc-100">API Usage Breakdown</h3>
                </div>
                <button className="text-[10px] font-bold text-blue-500 flex items-center gap-1 hover:underline">
                  Full Analytics <ExternalLink className="w-3 h-3" />
                </button>
              </div>
              <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-3xl overflow-hidden shadow-sm">
                <div className="divide-y divide-zinc-100 dark:divide-zinc-800">
                  {apiCosts.length > 0 ? apiCosts.map((api) => (
                    <div key={api.id} className="p-5 flex items-center justify-between hover:bg-zinc-50 dark:hover:bg-zinc-800/30 transition-colors group">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-2xl bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center text-zinc-400 group-hover:text-violet-500 transition-colors">
                          <Cpu className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="text-xs font-bold text-zinc-800 dark:text-zinc-100">{api.name}</p>
                          <p className="text-[10px] text-zinc-400 font-medium">{api.usage}</p>
                        </div>
                      </div>
                      <p className="text-sm font-black text-zinc-800 dark:text-zinc-50">${api.cost.toFixed(2)}</p>
                    </div>
                  )) : (
                    <div className="p-10 text-center">
                      <Info className="w-8 h-8 text-zinc-300 mx-auto mb-2" />
                      <p className="text-xs text-zinc-400 font-medium italic">No recent API usage detected</p>
                    </div>
                  )}
                </div>
              </div>
            </section>

            {/* Billing Management */}
            <section className="space-y-6">
              <div className="flex items-center gap-2 ml-1">
                <CreditCard className="w-5 h-5 text-emerald-500 fill-emerald-500/20" />
                <h3 className="text-lg font-bold text-zinc-800 dark:text-zinc-100">Payment Methods</h3>
              </div>
              <div className="space-y-4">
                <div className="p-5 rounded-3xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 shadow-sm flex items-center justify-between group cursor-pointer hover:border-zinc-300 dark:hover:border-zinc-700 transition-all">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-8 rounded-lg bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center border border-zinc-200 dark:border-zinc-700">
                      <span className="text-[10px] font-black text-zinc-400 tracking-widest uppercase">VISA</span>
                    </div>
                    <div>
                      <p className="text-xs font-bold text-zinc-800 dark:text-zinc-100">•••• •••• •••• 4242</p>
                      <p className="text-[9px] text-zinc-400 uppercase font-black tracking-widest">Expires 12/28</p>
                    </div>
                  </div>
                  <Badge className="bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 font-black text-[9px] uppercase px-2 py-0.5 rounded-full group-hover:bg-zinc-200 dark:group-hover:bg-zinc-700">Primary</Badge>
                </div>

                <div className="p-6 rounded-3xl border-2 border-dashed border-zinc-200 dark:border-zinc-800 flex flex-col items-center justify-center gap-2 hover:border-blue-400 dark:hover:border-blue-900 transition-all cursor-pointer group py-8 bg-zinc-50/30 dark:bg-zinc-900/10">
                  <div className="p-2 rounded-full bg-zinc-100 dark:bg-zinc-800 text-zinc-400 group-hover:text-blue-500 group-hover:bg-blue-500/10 transition-all">
                    <CreditCard className="w-4 h-4" />
                  </div>
                  <span className="text-[11px] font-black text-zinc-500 dark:text-zinc-400 uppercase tracking-widest group-hover:text-blue-500 transition-colors">Add New Method</span>
                </div>
              </div>

              {/* Invoices */}
              <div className="mt-8">
                <div className="flex items-center justify-between mb-4 ml-1">
                  <div className="flex items-center gap-2">
                    <History className="w-5 h-5 text-zinc-400 fill-zinc-400/20" />
                    <h3 className="text-lg font-bold text-zinc-800 dark:text-zinc-100">Recent Invoices</h3>
                  </div>
                  <button className="text-[10px] font-bold text-zinc-400 hover:text-zinc-600 transition-colors">View All Invoices</button>
                </div>
                <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-3xl overflow-hidden shadow-sm divide-y divide-zinc-100 dark:divide-zinc-800">
                  {[
                    { id: 'INV-041', date: 'Apr 01, 2026', amount: '$450.00', status: 'Paid' },
                    { id: 'INV-040', date: 'Mar 01, 2026', amount: '$420.50', status: 'Paid' },
                    { id: 'INV-039', date: 'Feb 01, 2026', amount: '$389.20', status: 'Paid' }
                  ].map((inv) => (
                    <div key={inv.id} className="p-4 flex items-center justify-between hover:bg-zinc-50 dark:hover:bg-zinc-800/30 transition-colors group cursor-pointer">
                      <div className="flex flex-col">
                        <span className="text-xs font-bold text-zinc-800 dark:text-zinc-100">{inv.id}</span>
                        <span className="text-[10px] text-zinc-400 font-medium">{inv.date}</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-xs font-bold text-zinc-800 dark:text-zinc-100">{inv.amount}</span>
                        <Badge className="bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 text-[9px] uppercase font-black rounded-full border border-emerald-100 dark:border-emerald-800/50">
                          {inv.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </div>

          {/* Quick Help */}
          <section className="bg-blue-600 rounded-[2.5rem] p-10 overflow-hidden relative group">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl group-hover:scale-125 transition-transform duration-700" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-400/20 rounded-full translate-y-1/3 -translate-x-1/3 blur-2xl group-hover:scale-110 transition-transform duration-500" />
            
            <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="text-center md:text-left">
                <h3 className="text-2xl font-black text-white tracking-tight">Need a custom plan?</h3>
                <p className="text-blue-100 text-sm mt-2 max-w-sm leading-relaxed">
                  We offer customized solutions for high-volume API consumers and enterprise teams. 
                  Get in touch with our solutions experts for a quote.
                </p>
              </div>
              <button className="px-8 py-4 bg-white text-blue-600 rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-zinc-50 transition-all shadow-xl shadow-blue-900/20 whitespace-nowrap">
                Talk To Support
              </button>
            </div>
          </section>

        </div>
      </ScrollArea>
    </div>
  );
}
